import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { listRequestFiles } from '../../../api/requests/index.js';
import {
  WorkspaceTabStrip,
  type IconName,
  type WorkspaceTabDescriptor,
} from '../../../ui/index.js';
import { entityKey, useNotificationsByEntity } from '../../notifications/index.js';
import { requestsKeys } from '../queries.js';
import type { RequestView } from '../types.js';
import { REQUEST_DRAWER_TABS, type RequestDrawerTabId } from '../useRequestUrlState.js';

// INVARIANT: the narrow visible content is `<Icon> N` (bare number,
//   no parentheses) for every counted tab. The accessible name MUST
//   stay identical to the desktop localized `tabs.*` strings (e.g.
//   items "<localized> (5)") so existing role/name queries and screen
//   readers keep reading the same string.
const TAB_ICON_BY_ID: Record<RequestDrawerTabId, IconName> = {
  items: 'package',
  carriers: 'ship',
  chat: 'message-square',
  audit: 'history',
  files: 'paperclip',
  archive: 'archive',
};

export function RequestDrawerTabs({
  r,
  tab,
  setTab,
  narrow,
  archiveCount,
}: {
  r: RequestView;
  tab: RequestDrawerTabId;
  setTab: (t: RequestDrawerTabId) => void;
  narrow: boolean;
  archiveCount: number;
}) {
  const { t } = useTranslation('requests');
  const filesQuery = useQuery({
    queryKey: requestsKeys.files(r.id),
    queryFn: () => listRequestFiles(r.id),
  });
  const fileCount =
    filesQuery.isLoading || filesQuery.isError ? null : (filesQuery.data?.length ?? 0);
  // INVARIANT (#1072): tab badge sums = base count + unread-notification
  //   aggregates routed to this tab. The shared list query backs every
  //   tab strip; opening a tab fires `useMarkNotificationViewedByTarget`
  //   in the drawer, so the unread offset zeroes the moment the tab is
  //   active.
  const { byEntity } = useNotificationsByEntity();
  const summary = byEntity.get(entityKey('CHINA_REQUEST', r.id));
  const desktopLabels: Record<RequestDrawerTabId, string> = {
    items: t('tabs.items', { count: r.items.length }),
    carriers: t('tabs.carriers', { count: r.carriers.length }),
    chat: t('tabs.chat', { count: r.chat.length }),
    audit: t('tabs.auditCount', { count: r.audit.length }),
    files: fileCount === null ? t('tabs.files') : t('tabs.filesCount', { count: fileCount }),
    archive: archiveCount > 0 ? t('tabs.archiveCount', { count: archiveCount }) : t('tabs.archive'),
  };
  const tabNotifications: Record<RequestDrawerTabId, number> = {
    items: summary?.tabs.items ?? 0,
    carriers: summary?.tabs.carriers ?? 0,
    chat: summary?.tabs.chat ?? 0,
    audit: summary?.tabs.audit ?? 0,
    files: 0,
    archive: 0,
  };
  const tabCounts: Record<RequestDrawerTabId, number | null> = {
    items: r.items.length,
    carriers: r.carriers.length,
    chat: r.chat.length,
    audit: r.audit.length,
    files: fileCount,
    archive: archiveCount > 0 ? archiveCount : null,
  };
  // Files tab is available for all China request kinds incl. SIMPLE (#2102).
  const tabs: WorkspaceTabDescriptor<RequestDrawerTabId>[] = REQUEST_DRAWER_TABS.map((id) => ({
    id,
    label: desktopLabels[id],
    icon: TAB_ICON_BY_ID[id],
    count: tabCounts[id],
    notificationCount: tabNotifications[id],
    controls: `request-drawer-tabpanel-${id}`,
    buttonId: `request-drawer-tab-${id}`,
    testId: `request-drawer-tab-${id}`,
  }));
  return <WorkspaceTabStrip tabs={tabs} activeId={tab} onSelect={setTab} narrow={narrow} />;
}
